// Admin Complaint Detail — split layout
// LEFT: thread (reuses /admin/support reply endpoints)
// RIGHT: tabs = AI Triage | Customer | Compensation
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Send, Loader2, Sparkles, User, Wallet, BookOpen, ShieldAlert, AlertTriangle,
    BadgeCheck, ArrowUpRight, CheckCircle2, Banknote, Ticket, BadgePercent, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import SendbirdEmbeddedChannel from "../../components/sendbird/SendbirdEmbeddedChannel";
import { api } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";

const SEVERITY_TONE = {
    low: "border-slate-200 bg-slate-50 text-slate-700",
    medium: "border-amber-200 bg-amber-50 text-amber-800",
    high: "border-orange-200 bg-orange-50 text-orange-800",
    critical: "border-rose-300 bg-rose-50 text-rose-800",
};

const STATUS_TONE = {
    open: "bg-emerald-100 text-emerald-700",
    awaiting_customer: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-700",
    awaiting_agent: "bg-blue-100 text-blue-700",
    resolved: "bg-zinc-100 text-zinc-700",
    closed: "bg-zinc-200 text-zinc-600",
};

// AI-suggested defaults by severity (rupees)
const COMPENSATION_DEFAULTS = { low: 50, medium: 150, high: 500, critical: 1000 };

const AdminComplaintDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: admin } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [activeTab, setActiveTab] = useState("triage"); // triage | customer | compensation
    const [busy, setBusy] = useState(false);
    const threadRef = useRef(null);

    const load = async () => {
        try {
            const { data: d } = await api.get(`/admin/complaints/${id}`);
            setData(d);
        } catch (err) {
            if (err?.response?.status === 404) {
                toast.error("Complaint not found");
                navigate("/admin/complaints");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, [data?.messages?.length]);

    const ticket = data?.ticket;
    const messages = data?.messages || [];
    const customer = data?.customer || {};
    const triage = ticket?.ai_triage || {};
    const sev = ticket?.severity_admin || ticket?.severity_auto || "medium";

    const send = async () => {
        const content = reply.trim();
        if (!content || sending) return;
        try {
            setSending(true);
            await api.post(`/admin/support/tickets/${id}/messages`, { content });
            setReply("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not send");
        } finally {
            setSending(false);
        }
    };

    const setSeverity = async (severity) => {
        try {
            setBusy(true);
            await api.post(`/admin/complaints/${id}/severity`, { severity });
            toast.success(`Severity → ${severity}`);
            await load();
        } catch {
            toast.error("Could not update severity");
        } finally {
            setBusy(false);
        }
    };

    const escalateMgr = async () => {
        if (!window.confirm("Escalate this complaint to manager (priority → urgent)?")) return;
        try {
            setBusy(true);
            await api.post(`/admin/complaints/${id}/escalate-to-manager`);
            toast.success("Escalated to manager");
            await load();
        } catch {
            toast.error("Escalation failed");
        } finally {
            setBusy(false);
        }
    };

    const resolveTicket = async () => {
        const notes = window.prompt("Resolution notes (will be visible to the customer):");
        if (!notes || notes.trim().length < 2) return;
        try {
            setBusy(true);
            await api.post(`/admin/support/tickets/${id}/resolve`, { resolution_notes: notes.trim() });
            toast.success("Complaint resolved");
            await load();
        } catch {
            toast.error("Could not resolve");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="h-96 skeleton rounded-3xl" />;
    }
    if (!ticket) return null;

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr,420px]" data-testid="admin-complaint-detail">
            {/* LEFT — Thread */}
            <div className="rounded-3xl border border-zinc-100 bg-white shadow-sm">
                <div className="border-b border-zinc-100 p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-mono text-xs text-zinc-400">{ticket.ticket_no}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[ticket.status] || "bg-zinc-100 text-zinc-700"}`}>
                                    {(ticket.status || "").replace("_", " ")}
                                </span>
                                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_TONE[sev]}`}>
                                    {sev}
                                </span>
                                {ticket.complaint_subtype && (
                                    <span className="rounded-full bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
                                        {ticket.complaint_subtype.replace(/_/g, " ")}
                                    </span>
                                )}
                            </div>
                            <h2 className="mt-1.5 text-lg font-semibold text-zinc-900">{ticket.subject}</h2>
                            {ticket.compensation_issued && (
                                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-800" data-testid="admin-comp-issued-badge">
                                    <CheckCircle2 className="h-3 w-3" /> Compensated ₹{(ticket.compensation_total || 0).toFixed(0)}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            <button onClick={escalateMgr} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50" data-testid="admin-escalate-mgr">
                                <ShieldAlert className="h-3 w-3" /> Manager
                            </button>
                            {ticket.status !== "resolved" && ticket.status !== "closed" && (
                                <button onClick={resolveTicket} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50" data-testid="admin-resolve">
                                    <BadgeCheck className="h-3 w-3" /> Resolve
                                </button>
                            )}
                            <button onClick={load} className="rounded-full p-1.5 text-zinc-500 hover:bg-zinc-100" aria-label="Refresh" data-testid="admin-refresh">
                                <RefreshCw className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Phase 2b — Sendbird real-time chat with customer (staff view) */}
                <div className="border-b border-zinc-100 p-5" data-testid="admin-complaint-sendbird-section">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Live chat</p>
                            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Talk to the customer in real-time</h3>
                        </div>
                        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Sendbird</span>
                    </div>
                    <SendbirdEmbeddedChannel
                        channelUrl={`pills_support_${ticket.id}`}
                        customType="support_ticket"
                        height="380px"
                        testId="sendbird-support-embed-staff"
                        emptyMessage="Setting up live chat…"
                    />
                </div>

                {/* Thread */}
                <div ref={threadRef} className="h-[480px] space-y-3 overflow-y-auto bg-zinc-50/40 p-5" data-testid="admin-complaint-thread">
                    {messages.map((m) => {
                        const isCustomer = m.sender_type === "customer";
                        const isSystem = m.sender_type === "system";
                        if (isSystem) {
                            return (
                                <div key={m.id} className="my-2 text-center">
                                    <span className="rounded-full bg-zinc-200 px-3 py-0.5 text-[10px] uppercase tracking-wider text-zinc-600">{m.content}</span>
                                </div>
                            );
                        }
                        return (
                            <div key={m.id} className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}>
                                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                                    isCustomer ? "bg-white text-zinc-900 ring-1 ring-zinc-100" : "bg-brand-700 text-white"
                                }`}>
                                    <p className="whitespace-pre-wrap break-words">{m.content}</p>
                                    <p className={`mt-1 text-[10px] ${isCustomer ? "text-zinc-400" : "text-white/70"}`}>
                                        {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="border-t border-zinc-100 p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
                            placeholder="Reply to customer… (Ctrl+Enter to send)"
                            rows={2}
                            maxLength={4000}
                            className="flex-1 resize-none rounded-2xl border border-zinc-200 p-3 text-sm outline-none focus:border-brand-500"
                            data-testid="admin-reply-input"
                        />
                        <button
                            onClick={send}
                            disabled={!reply.trim() || sending}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-700 text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
                            aria-label="Send"
                            data-testid="admin-send-btn"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                    </div>
                    {triage.ai_drafted_response && (
                        <button
                            onClick={() => setReply((r) => r ? r : triage.ai_drafted_response)}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] text-brand-700 hover:underline"
                            data-testid="admin-use-ai-draft"
                            title={triage.ai_drafted_response}
                        >
                            <Sparkles className="h-3 w-3" /> Use AI-drafted reply
                        </button>
                    )}
                </div>
            </div>

            {/* RIGHT — Tabs */}
            <div className="rounded-3xl border border-zinc-100 bg-white shadow-sm">
                <nav className="flex border-b border-zinc-100 text-sm" data-testid="admin-complaint-tabs">
                    {[
                        { id: "triage", icon: Sparkles, label: "AI Triage" },
                        { id: "customer", icon: User, label: "Customer" },
                        { id: "compensation", icon: Wallet, label: "Compensate" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-3 text-xs font-semibold transition ${
                                activeTab === t.id
                                    ? "border-brand-600 text-brand-700"
                                    : "border-transparent text-zinc-500 hover:text-zinc-900"
                            }`}
                            data-testid={`tab-${t.id}`}
                        >
                            <t.icon className="h-3.5 w-3.5" /> {t.label}
                        </button>
                    ))}
                </nav>

                <div className="p-5">
                    {activeTab === "triage" && (
                        <TriageTab
                            triage={triage}
                            severity={sev}
                            severityAuto={ticket.severity_auto}
                            severityAdmin={ticket.severity_admin}
                            onSetSeverity={setSeverity}
                            busy={busy}
                            requiresCallback={ticket.requires_callback}
                            ticketSentiment={ticket.sentiment}
                        />
                    )}
                    {activeTab === "customer" && <CustomerTab customer={customer} />}
                    {activeTab === "compensation" && (
                        <CompensationTab
                            complaintId={id}
                            defaultAmount={COMPENSATION_DEFAULTS[sev] || 100}
                            severity={sev}
                            issued={ticket.compensation_issued}
                            issuedTotal={ticket.compensation_total}
                            onIssued={load}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const TriageTab = ({ triage, severity, severityAuto, severityAdmin, onSetSeverity, busy, requiresCallback, ticketSentiment }) => {
    if (!triage || !triage.attempted) {
        return <p className="text-sm text-zinc-500">No AI triage data on this complaint.</p>;
    }
    const sentimentLabel = ticketSentiment?.label || triage.sentiment_label || "—";
    return (
        <div className="space-y-4" data-testid="admin-triage-tab">
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">AI auto severity</p>
                <p className="mt-1 text-sm text-zinc-900">{severityAuto || "—"} {severityAdmin && <span className="ml-2 text-xs text-zinc-500">(admin override: <strong>{severityAdmin}</strong>)</span>}</p>
            </div>
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Override severity</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                    {["low", "medium", "high", "critical"].map((s) => (
                        <button
                            key={s}
                            onClick={() => onSetSeverity(s)}
                            disabled={busy || s === severity}
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${s === severity ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-200 hover:border-brand-400"}`}
                            data-testid={`override-severity-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Sentiment</p>
                <p className="mt-1 text-sm text-zinc-900">{sentimentLabel}</p>
            </div>

            <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Root cause hypothesis</p>
                <p className="mt-1 text-sm leading-relaxed text-zinc-700">{triage.root_cause_hypothesis || "—"}</p>
            </div>

            {triage.suggested_resolution_path?.length > 0 && (
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Suggested resolution steps</p>
                    <ul className="mt-1 space-y-1 text-sm text-zinc-700">
                        {triage.suggested_resolution_path.slice(0, 6).map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                </div>
            )}

            {triage.knowledge_articles_shown?.length > 0 && (
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">KB articles shown</p>
                    <ul className="mt-1 space-y-1 text-xs">
                        {triage.knowledge_articles_shown.map((s) => (
                            <li key={s}>
                                <Link to={`/help/kb?slug=${s}`} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">
                                    <BookOpen className="mr-1 inline h-3 w-3" />{s}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {triage.urgency_minutes != null && (
                    <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-semibold text-zinc-700">
                        Suggested SLA: {triage.urgency_minutes} min
                    </span>
                )}
                {requiresCallback && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
                        <AlertTriangle className="mr-1 inline h-3 w-3" /> Callback recommended
                    </span>
                )}
                {triage.fallback_used && (
                    <span className="rounded-full bg-zinc-200 px-2.5 py-1 text-[11px] font-semibold text-zinc-700">
                        Heuristic fallback (no LLM)
                    </span>
                )}
            </div>
        </div>
    );
};

const CustomerTab = ({ customer }) => (
    <div className="space-y-4 text-sm" data-testid="admin-customer-tab">
        <div>
            <p className="text-base font-semibold text-zinc-900">{customer.full_name || "—"}</p>
            <p className="text-zinc-500">{customer.email}</p>
            {customer.pills_id && <p className="font-mono text-[11px] text-zinc-400">{customer.pills_id}</p>}
        </div>
        {customer.phone && <p className="text-xs text-zinc-600">📞 {customer.phone}</p>}
        {customer.elite?.active && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                Elite member
            </span>
        )}
        <div className="grid grid-cols-3 gap-2 pt-2">
            <Stat label="Orders" value={customer.n_orders} />
            <Stat label="LTV" value={`₹${(customer.lifetime_value || 0).toFixed(0)}`} />
            <Stat label="Prior complaints" value={customer.n_prior_complaints} />
        </div>
        {customer.created_at && (
            <p className="text-xs text-zinc-500">Joined: {new Date(customer.created_at).toLocaleDateString()}</p>
        )}
        <Link to={`/admin/users/${customer.id || ""}`} className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline" data-testid="admin-view-customer-link">
            <ArrowUpRight className="h-3 w-3" /> Full customer profile
        </Link>
    </div>
);

const Stat = ({ label, value }) => (
    <div className="rounded-lg bg-zinc-50 p-2 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="mt-0.5 text-sm font-semibold text-zinc-900">{value ?? 0}</p>
    </div>
);

const CompensationTab = ({ complaintId, defaultAmount, severity, issued, issuedTotal, onIssued }) => {
    const [type, setType] = useState("wallet_credit");
    const [amount, setAmount] = useState(defaultAmount);
    const [note, setNote] = useState(`Apologies for the ${severity} issue — please accept this on us.`);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { setAmount(defaultAmount); }, [defaultAmount]);

    const submit = async () => {
        if (!amount || amount <= 0) {
            toast.error("Enter a positive amount");
            return;
        }
        if (!window.confirm(`Issue ₹${amount} via ${type.replace("_", " ")}?`)) return;
        try {
            setSubmitting(true);
            await api.post(`/admin/complaints/${complaintId}/issue-compensation`, {
                type,
                amount: Number(amount),
                note: note.trim() || undefined,
            });
            toast.success("Compensation issued");
            onIssued?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not issue");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-4 text-sm" data-testid="admin-compensation-tab">
            {issued && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                    <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Previously issued: ₹{(issuedTotal || 0).toFixed(0)}
                </div>
            )}

            <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Type</p>
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {[
                        { id: "wallet_credit", label: "Wallet", icon: Banknote },
                        { id: "coupon", label: "Coupon", icon: BadgePercent },
                        { id: "refund", label: "Refund", icon: Ticket },
                    ].map((opt) => (
                        <button
                            key={opt.id}
                            onClick={() => setType(opt.id)}
                            className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2 text-[11px] font-semibold transition ${
                                type === opt.id ? "border-brand-600 bg-brand-50 text-brand-700" : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300"
                            }`}
                            data-testid={`comp-type-${opt.id}`}
                        >
                            <opt.icon className="h-4 w-4" />
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500" htmlFor="comp-amount">
                    Amount (₹) <span className="font-normal italic text-zinc-400">— AI suggested ₹{COMPENSATION_DEFAULTS[severity] ?? "—"} for {severity}</span>
                </label>
                <input
                    id="comp-amount"
                    type="number"
                    min="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                    data-testid="comp-amount-input"
                />
                <div className="mt-1.5 flex flex-wrap gap-1">
                    {[100, 250, 500, 1000].map((q) => (
                        <button
                            key={q}
                            onClick={() => setAmount(q)}
                            className="rounded-full border border-zinc-200 px-2 py-0.5 text-[10px] hover:border-brand-400"
                            data-testid={`comp-quick-${q}`}
                        >₹{q}</button>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500" htmlFor="comp-note">
                    Note to customer
                </label>
                <textarea
                    id="comp-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    maxLength={300}
                    className="mt-1 w-full resize-none rounded-lg border border-zinc-200 p-2 text-sm outline-none focus:border-brand-500"
                    data-testid="comp-note-input"
                />
            </div>

            <button
                onClick={submit}
                disabled={submitting}
                className="w-full rounded-full bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:opacity-50"
                data-testid="comp-submit"
            >
                {submitting ? <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" /> : null}
                Issue ₹{amount || 0} compensation
            </button>
            <p className="text-[10px] text-zinc-500">
                Wallet credits are instant. Coupons are single-use codes restricted to this customer. Refunds create a pending payment-team obligation.
            </p>
        </div>
    );
};

const AdminComplaintDetail = () => (
    <AdminAuthGuard>
        <AdminLayout
            title="Complaint detail"
            subtitle="AI triage · customer 360 · one-click compensation"
            action={
                <Link to="/admin/complaints" className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-zinc-300" data-testid="admin-back-link">
                    <ArrowLeft className="h-3 w-3" /> All complaints
                </Link>
            }
        >
            <AdminComplaintDetailContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminComplaintDetail;
